import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CartSidebar from '../../components/feature/CartSidebar';
import SearchModal from '../../components/feature/SearchModal';
import LoginModal from '../../components/feature/LoginModal';
import { useAuth } from '../../contexts/AuthContext';

export default function ProfilePage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginMode, setLoginMode] = useState<'login' | 'register'>('login');
  const { user, signOut, loading } = useAuth();

  const openLogin = (mode: 'login' | 'register' = 'login') => {
    setLoginMode(mode);
    setLoginOpen(true);
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Kullanıcı';
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-cream-light font-sans">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => openLogin('login')} />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">

          {loading ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-16 h-16 bg-cream rounded-full mx-auto mb-4 animate-pulse" />
              <div className="h-4 bg-cream rounded w-32 mx-auto" />
            </div>
          ) : user ? (
            <div className="space-y-4">
              {/* Profile Card */}
              <div className="bg-white rounded-3xl p-8">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 flex items-center justify-center bg-gold rounded-full text-white text-xl font-bold flex-shrink-0">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="font-serif text-xl font-bold text-coffee truncate">{displayName}</h1>
                    <p className="text-sm text-coffee-light truncate">{user.email}</p>
                    <p className="text-xs text-coffee-light mt-1">
                      Üyelik: {new Date(user.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-shopping-bag-line', label: 'Siparişlerim', desc: 'Geçmiş siparişleri görüntüle', href: '/siparislerim' },
                  { icon: 'ri-heart-line', label: 'Favorilerim', desc: 'Beğendiğin ürünler', href: '/favorilerim' },
                  { icon: 'ri-map-pin-line', label: 'Adreslerim', desc: 'Kayıtlı adreslerini yönet', href: '/adreslerim' },
                ].map(item => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="bg-white rounded-2xl p-5 hover:bg-cream transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-xl mb-3 group-hover:bg-gold/20 transition-colors">
                      <i className={`${item.icon} text-gold text-lg`} />
                    </div>
                    <p className="font-semibold text-sm text-coffee mb-0.5">{item.label}</p>
                    <p className="text-xs text-coffee-light">{item.desc}</p>
                  </Link>
                ))}
              </div>

              {/* Account Info */}
              <div className="bg-white rounded-3xl p-6">
                <h2 className="font-serif text-base font-semibold text-coffee mb-4">Hesap Bilgileri</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b border-cream-dark">
                    <div>
                      <p className="text-xs text-coffee-light mb-0.5">Ad Soyad</p>
                      <p className="text-sm font-medium text-coffee">{user.user_metadata?.full_name || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-xs text-coffee-light mb-0.5">E-posta</p>
                      <p className="text-sm font-medium text-coffee">{user.email}</p>
                    </div>
                    {user.email_confirmed_at ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
                        <i className="ri-checkbox-circle-line" /> Doğrulandı
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
                        <i className="ri-time-line" /> Bekliyor
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Sign Out */}
              <button
                onClick={signOut}
                className="w-full py-3.5 border-2 border-red-200 text-red-500 font-semibold rounded-2xl hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-logout-box-r-line" />
                  Çıkış Yap
                </span>
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-cream rounded-full mx-auto mb-6">
                <i className="ri-user-3-line text-3xl text-coffee-light" />
              </div>
              <h1 className="font-serif text-2xl font-bold text-coffee mb-3">Hesabım</h1>
              <p className="text-coffee-light mb-8 max-w-sm mx-auto text-sm leading-relaxed">
                Profil, adres defteri, favoriler ve sipariş geçmişinize erişmek için giriş yapın.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => openLogin('login')}
                  className="px-8 py-3 bg-coffee text-white font-semibold rounded-xl hover:bg-coffee-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Giriş Yap
                </button>
                <button
                  onClick={() => openLogin('register')}
                  className="px-8 py-3 border-2 border-coffee text-coffee font-semibold rounded-xl hover:bg-coffee hover:text-white transition-all whitespace-nowrap cursor-pointer"
                >
                  Üye Ol
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <CartSidebar />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} initialMode={loginMode} />
    </div>
  );
}
